import { useState } from "react";
import { Alert, Box, IconButton, Tooltip, Typography } from "@mui/material";
import BaseModal from "@components/common/HPBaseModal";
import HPButton from "@components/common/Button";
import HPTag, { HPTagColor } from "@components/common/Tag";
import { TrashIcon } from "@/assets/images";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import { snackbarDispatcher } from "@/store/slices/snackbar-slice";
import { t } from "@utils/i18n";
import { capitalize } from "@utils/mappings";
import { ModalProps } from "@/_constants/modal";
import { DatasetElementKind, VisibilityActionType, VisibilityTarget } from "@models/Dataset";
import { SelectionItem } from "@hooks/use-dataset-questions-selection";

interface SingleTarget {
  kind: DatasetElementKind;
  label: string;
}

interface VisibilityReviewModalProps extends ModalProps {
  action: VisibilityActionType;
  isBulk: boolean;
  target?: SingleTarget;
  affectedCounts: { questions: number; variables: number; values: number };
  initialTargets: VisibilityTarget[];
  selectionItems?: SelectionItem[];
  selectionSpansPages?: boolean;
  onConfirm: (targets: VisibilityTarget[]) => Promise<void>;
}

const titleFor = (action: VisibilityActionType, isBulk: boolean, target?: SingleTarget) => {
  if (action === "show_question") return t("pages.datasetDetails.visibilityReviewShowQuestionTitle");
  if (isBulk) {
    return action === "hide"
      ? t("pages.datasetDetails.visibilityReviewHideSelected")
      : t("pages.datasetDetails.visibilityReviewShowSelected");
  }
  const verb =
    action === "hide"
      ? t("pages.datasetDetails.visibilityReviewHideTitle")
      : t("pages.datasetDetails.visibilityReviewShowTitle");
  return target ? `${verb} "${target.label}"` : verb;
};

const bodyFor = (
  action: VisibilityActionType,
  isBulk: boolean,
  counts: { questions: number; variables: number; values: number },
  target?: SingleTarget,
) => {
  if (isBulk) {
    return t("pages.datasetDetails.visibilityReviewBulkBody", {
      questions: counts.questions,
      variables: counts.variables,
      values: counts.values,
    });
  }
  if (!target) return "";
  const label = `"${target.label}"`;
  if (action === "show_question") {
    return t("pages.datasetDetails.visibilityReviewShowQuestionBody", { label });
  }
  if (target.kind === "question") {
    return t("pages.datasetDetails.visibilityReviewHideQuestionBody", { label });
  }
  if (target.kind === "variable") {
    return action === "hide"
      ? t("pages.datasetDetails.visibilityReviewHideVariableBody", { label })
      : t("pages.datasetDetails.visibilityReviewShowVariableBody", { label });
  }
  return action === "hide"
    ? t("pages.datasetDetails.visibilityReviewHideValueBody", { label })
    : t("pages.datasetDetails.visibilityReviewShowValueBody", { label });
};

const formatAffectedSummary = (counts: {
  questions: number;
  variables: number;
  values: number;
}): string => {
  const parts: string[] = [];
  if (counts.questions > 0) {
    parts.push(
      t(
        counts.questions === 1
          ? "pages.datasetDetails.selectedQuestionsCount"
          : "pages.datasetDetails.selectedQuestionsCountPlural",
        { count: counts.questions },
      ),
    );
  }
  if (counts.variables > 0) {
    parts.push(
      t(
        counts.variables === 1
          ? "pages.datasetDetails.selectedVariablesCount"
          : "pages.datasetDetails.selectedVariablesCountPlural",
        { count: counts.variables },
      ),
    );
  }
  if (counts.values > 0) {
    parts.push(
      t(
        counts.values === 1
          ? "pages.datasetDetails.selectedValuesCount"
          : "pages.datasetDetails.selectedValuesCountPlural",
        { count: counts.values },
      ),
    );
  }
  return parts.join(", ");
};

/** Groups items by their parent question id, preserving first-seen order. */
const groupByQuestion = (items: SelectionItem[]): Map<string, SelectionItem[]> => {
  const groups = new Map<string, SelectionItem[]>();
  items.forEach((item) => {
    const key = item.questionId ?? "";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });
  return groups;
};

export default function VisibilityReviewModal({
  action,
  isBulk,
  target,
  affectedCounts,
  initialTargets,
  selectionItems,
  selectionSpansPages = false,
  onConfirm,
}: VisibilityReviewModalProps) {
  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local-only trim of the bulk selection, so the researcher can remove an
  // unintended item before confirming without touching the live selection
  // (Cancel must leave the real selection untouched).
  const [items, setItems] = useState<SelectionItem[]>(selectionItems ?? []);

  const handleRemoveItem = (item: SelectionItem) => {
    setItems((prev) =>
      prev.filter((candidate) => {
        if (candidate.id === item.id && candidate.kind === item.kind) return false;
        // Removing a whole question also drops the children it was covering.
        if (item.kind === "question" && candidate.questionId === item.id) return false;
        return true;
      }),
    );
  };

  const liveCounts = isBulk
    ? {
        questions: items.filter((i) => i.kind === "question").length,
        variables: items.filter((i) => i.kind === "variable").length,
        values: items.filter((i) => i.kind === "value").length,
      }
    : affectedCounts;

  const effectiveTargets: VisibilityTarget[] = isBulk
    ? items.map((item) => ({ id: item.id, kind: item.kind }))
    : initialTargets;

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(effectiveTargets);
      closeTopModal();
      snackbarDispatcher(dispatch).showSuccessSnackbar({
        message: t(
          action === "hide"
            ? "pages.datasetDetails.visibilitySavedHide"
            : "pages.datasetDetails.visibilitySavedShow",
          { summary: formatAffectedSummary(liveCounts) },
        ),
      });
    } catch {
      setError(t("pages.datasetDetails.visibilityReviewSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const confirmLabel =
    action === "hide" ? t("pages.datasetDetails.hideContent") : t("pages.datasetDetails.showContent");

  const removableItems = items.filter((item) => !item.includedWithQuestion);
  const removableQuestions = removableItems.filter((item) => item.kind === "question");
  const variableGroups = groupByQuestion(removableItems.filter((item) => item.kind === "variable"));
  const valueGroups = groupByQuestion(removableItems.filter((item) => item.kind === "value"));

  const renderRemoveButton = (item: SelectionItem) => (
    <IconButton
      size="small"
      aria-label={t("pages.datasetDetails.removeFromSelection")}
      onClick={() => handleRemoveItem(item)}
      sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
    >
      <TrashIcon className="w-4 h-4" />
    </IconButton>
  );

  // Used for standalone rows (the Questions section) — shows the item's own
  // type/title alongside its label since there's no group header to carry it.
  const renderStandaloneRow = (item: SelectionItem) => (
    <Box
      key={`${item.kind}-${item.id}`}
      className="flex items-center justify-between gap-2 py-1"
    >
      <Box className="flex items-center gap-2 min-w-0 flex-1">
        <Typography variant="body2" className="truncate">
          {item.label}
        </Typography>
        {item.contextType && (
          <Typography variant="caption" className="whitespace-nowrap text-hp-text-day-secondary">
            {capitalize(item.contextType)}
          </Typography>
        )}
        {item.contextTitle && (
          <Tooltip title={item.contextTitle}>
            <Typography
              variant="caption"
              className="max-w-[160px] truncate text-hp-text-day-secondary"
            >
              {item.contextTitle}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {renderRemoveButton(item)}
    </Box>
  );

  // Used for a variable/value row nested under its question group — the
  // group header carries the question's type/title, this row carries the
  // variable/value's own title.
  const renderChildRow = (item: SelectionItem) => (
    <Box
      key={`${item.kind}-${item.id}`}
      className="flex items-center justify-between gap-2 py-1 pl-2"
    >
      <Box className="flex items-center gap-2 min-w-0 flex-1">
        <Typography variant="body2" className="truncate">
          {item.label}
        </Typography>
        {item.title && item.title !== item.label && (
          <Tooltip title={item.title}>
            <Typography
              variant="caption"
              className="max-w-[220px] truncate text-hp-text-day-secondary"
            >
              {item.title}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {renderRemoveButton(item)}
    </Box>
  );

  const renderQuestionGroup = (questionId: string, groupItems: SelectionItem[]) => {
    const first = groupItems[0];
    return (
      <Box
        key={questionId}
        className="mb-2 rounded-md border border-hp-border-secondary p-2"
      >
        <Box className="mb-1 flex items-center gap-2">
          <HPTag ariaLabel={t("pages.datasetDetails.contextBadge")} color={HPTagColor.GRAY}>
            {t("pages.datasetDetails.contextBadge")}
          </HPTag>
          <Typography variant="body2" className="font-semibold truncate">
            {first.questionLabel}
          </Typography>
          {first.contextType && (
            <Typography variant="caption" className="whitespace-nowrap text-hp-text-day-secondary">
              {capitalize(first.contextType)}
            </Typography>
          )}
          {first.contextTitle && (
            <Tooltip title={first.contextTitle}>
              <Typography
                variant="caption"
                className="max-w-[160px] truncate text-hp-text-day-secondary"
              >
                {first.contextTitle}
              </Typography>
            </Tooltip>
          )}
        </Box>
        {groupItems.map(renderChildRow)}
      </Box>
    );
  };

  return (
    <BaseModal
      title={titleFor(action, isBulk, target)}
      confirmButtonText={confirmLabel}
      cancelButtonText={t("common.cancel")}
      onConfirm={handleConfirm}
      onCancel={closeTopModal}
      loading={saving}
      confirmDisabled={isBulk && items.length === 0}
      containerClassName="max-w-[760px]"
    >
      <Box className="flex flex-col gap-3">
        <Typography variant="body2">{bodyFor(action, isBulk, liveCounts, target)}</Typography>

        {isBulk && liveCounts.questions > 0 && (
          <Typography variant="body2" className="text-hp-text-day-secondary">
            {t("pages.datasetDetails.visibilityReviewWholeQuestionNote")}
          </Typography>
        )}

        {(isBulk || target?.kind === "question" || action === "show_question") && (
          <Typography variant="body2" className="text-hp-text-day-secondary">
            {t("pages.datasetDetails.visibilityReviewAffectedCounts", {
              questions: liveCounts.questions,
              variables: liveCounts.variables,
              values: liveCounts.values,
            })}
          </Typography>
        )}

        {isBulk && selectionSpansPages && (
          <Typography variant="body2" className="text-hp-text-day-secondary">
            {t("pages.datasetDetails.selectionSpansPages")}
          </Typography>
        )}

        {isBulk && removableItems.length > 0 && (
          <Box className="max-h-[32rem] overflow-y-auto rounded border border-hp-border-secondary p-2">
            {removableQuestions.length > 0 && (
              <Box className="mb-2">
                <Typography variant="caption" className="font-semibold text-hp-text-day-secondary">
                  {t("pages.datasetDetails.selectionListQuestionsHeader")}
                </Typography>
                {removableQuestions.map(renderStandaloneRow)}
              </Box>
            )}
            {variableGroups.size > 0 && (
              <Box className="mb-2">
                <Typography variant="caption" className="font-semibold text-hp-text-day-secondary">
                  {t("pages.datasetDetails.selectionListVariablesHeader")}
                </Typography>
                {Array.from(variableGroups.entries()).map(([questionId, groupItems]) =>
                  renderQuestionGroup(questionId, groupItems),
                )}
              </Box>
            )}
            {valueGroups.size > 0 && (
              <Box>
                <Typography variant="caption" className="font-semibold text-hp-text-day-secondary">
                  {t("pages.datasetDetails.selectionListValuesHeader")}
                </Typography>
                {Array.from(valueGroups.entries()).map(([questionId, groupItems]) =>
                  renderQuestionGroup(questionId, groupItems),
                )}
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <HPButton variant="text" size="small" onClick={handleConfirm}>
                {t("common.tryAgain")}
              </HPButton>
            }
          >
            {error}
          </Alert>
        )}
      </Box>
    </BaseModal>
  );
}
